// utils/getCachedImage.ts
// Fetches and caches an image in localStorage as base64. Returns a promise that resolves to the cached image (base64 or fallback).
import { USE_EMULATOR, EMULATOR_HOST, EMULATOR_PORTS } from "../config/emulator";

const fallbackSvg = `
<svg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'>
  <defs>
    <linearGradient id='bg' x1='0' y1='0' x2='1' y2='1'>
      <stop offset='0%' stop-color='#FDE68A'/>
      <stop offset='100%' stop-color='#FDBA74'/>
    </linearGradient>
  </defs>
  <circle cx='50' cy='50' r='48' fill='url(#bg)'/>
  <circle cx='35' cy='40' r='4' fill='#1F2937'/>
  <circle cx='65' cy='40' r='4' fill='#1F2937'/>
  <path d='M32 58c5 7 12 10 18 10s13-3 18-10' stroke='#1F2937' stroke-width='5' stroke-linecap='round' fill='none'/>
  <circle cx='27' cy='53' r='4' fill='#FCA5A5' opacity='0.75'/>
  <circle cx='73' cy='53' r='4' fill='#FCA5A5' opacity='0.75'/>
</svg>`;

const fallbackImage = `data:image/svg+xml;utf8,${encodeURIComponent(fallbackSvg)}`;

// Default cache expiration: 7 days (in ms)
export const DEFAULT_EXPIRATION = 7 * 24 * 60 * 60 * 1000;

interface CachedImage {
  data: string;
  timestamp: number;
}

// In-memory LRU cache for this session
const MAX_MEMORY_CACHE_SIZE = 50;
const memoryCache: Record<string, string> = {};
const memoryCacheOrder: string[] = [];

function setMemoryCache(key: string, value: string) {
  if (memoryCache[key]) {
    // Move to end (most recently used)
    const idx = memoryCacheOrder.indexOf(key);
    if (idx !== -1) memoryCacheOrder.splice(idx, 1);
  }
  memoryCache[key] = value;
  memoryCacheOrder.push(key);
  // Evict least recently used if over limit
  if (memoryCacheOrder.length > MAX_MEMORY_CACHE_SIZE) {
    const oldest = memoryCacheOrder.shift();
    if (oldest) delete memoryCache[oldest];
  }
}

function getMemoryCache(key: string): string | undefined {
  if (memoryCache[key]) {
    // Move to end (most recently used)
    const idx = memoryCacheOrder.indexOf(key);
    if (idx !== -1) memoryCacheOrder.splice(idx, 1);
    memoryCacheOrder.push(key);
    return memoryCache[key];
  }
  return undefined;
}

function deleteMemoryCache(key: string) {
  delete memoryCache[key];
  const idx = memoryCacheOrder.indexOf(key);
  if (idx !== -1) memoryCacheOrder.splice(idx, 1);
}

function getCacheObject(cacheKey: string): CachedImage | null {
  const raw = localStorage.getItem(cacheKey);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as CachedImage;
  } catch {
    return null;
  }
}

function getFallBackImage(initialLetter: string): string {
  if (!initialLetter || initialLetter.length === 0) {
    return fallbackImage;
  }
  let upperInitialLetter = initialLetter.charAt(0).toUpperCase();
  const primaryColorHex = "%23F59E0B"; // The %23 is the # character encoded
  const svgString = `
  <svg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'>
    <circle cx='50' cy='50' r='50' fill='${primaryColorHex}' fill-opacity='0.25'/>
    <circle cx='35' cy='62' r='3' fill='%23FCA5A5' fill-opacity='0.75'/>
    <circle cx='65' cy='62' r='3' fill='%23FCA5A5' fill-opacity='0.75'/>
    <text x='50' y='50' fill='${primaryColorHex}' font-family='sans-serif' font-size='45' font-weight='600' text-anchor='middle' dominant-baseline='central'>
      ${upperInitialLetter}
    </text>
  </svg>`.trim();

  // This converts the raw SVG into a valid Data URL string
  return `data:image/svg+xml;utf8,${encodeURIComponent(svgString)}`;
}

function hash(str: string): number {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) + hash + str.charCodeAt(i); // hash * 33 + c
  }
  return hash >>> 0; // Ensure unsigned
}

function normalizeImageUrlForEnvironment(imageUrl: string): string {
  if (!USE_EMULATOR || !imageUrl) return imageUrl;

  try {
    const parsed = new URL(imageUrl);
    const isFirebaseStorageHost =
      parsed.hostname === "firebasestorage.googleapis.com" ||
      parsed.hostname === "storage.googleapis.com";

    if (!isFirebaseStorageHost) return imageUrl;

    if (!parsed.pathname.startsWith("/v0/b/")) return imageUrl;

    parsed.protocol = "http:";
    parsed.hostname = EMULATOR_HOST;
    parsed.port = String(EMULATOR_PORTS.storage);

    return parsed.toString();
  } catch {
    return imageUrl;
  }
}

export async function getCachedImage(
  imageUrl: string,
  initialLetter: string = "",
  expirationMs: number = DEFAULT_EXPIRATION,
): Promise<string> {
  if (!imageUrl || imageUrl.trim().length === 0) {
    return getFallBackImage(initialLetter);
  }
  const normalizedImageUrl = normalizeImageUrlForEnvironment(imageUrl);
  let cacheKey = `imageCache_${hash(normalizedImageUrl)}`;
  // Check in-memory cache first
  const mem = getMemoryCache(cacheKey);
  if (mem) {
    return mem;
  }
  const cached = getCacheObject(cacheKey);
  const now = Date.now();
  if (cached && now - cached.timestamp < expirationMs) {
    setMemoryCache(cacheKey, cached.data);
    return cached.data;
  } else if (cached) {
    // Expired, remove
    localStorage.removeItem(cacheKey);
    deleteMemoryCache(cacheKey);
  }
  try {
    const res = await fetch(normalizedImageUrl, {
      headers: {
        accept:
          "image/avif,image/webp,image/apng,image/svg+xml,image/*,*/*;q=0.8",
        "sec-fetch-dest": "image",
        "sec-fetch-mode": "no-cors",
        "sec-fetch-site": "cross-site",
      },
      referrerPolicy: "no-referrer",
      method: "GET",
      mode: "cors",
    });
    if (!res.ok) {
      return getFallBackImage(initialLetter);
    }
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64data = reader.result as string;
        const cacheObj: CachedImage = { data: base64data, timestamp: now };
        localStorage.setItem(cacheKey, JSON.stringify(cacheObj));
        setMemoryCache(cacheKey, base64data);
        resolve(base64data);
      };
      reader.readAsDataURL(blob);
    });
  } catch {
    return getFallBackImage(initialLetter);
  }
}

// Clear cache for a specific user
export function clearImageCache(cacheKey: string) {
  localStorage.removeItem(cacheKey);
  deleteMemoryCache(cacheKey);
}

// Clear all image caches (keys starting with 'imageCache_')
export function clearAllImageCaches() {
  Object.keys(localStorage).forEach((key) => {
    if (key.startsWith("imageCache_")) {
      localStorage.removeItem(key);
      deleteMemoryCache(key);
    }
  });
}

// Optionally, clear expired caches on app load
export function clearExpiredImageCaches(
  expirationMs: number = DEFAULT_EXPIRATION,
) {
  const now = Date.now();
  Object.keys(localStorage).forEach((key) => {
    if (key.startsWith("imageCache_")) {
      const cached = getCacheObject(key);
      if (cached && now - cached.timestamp >= expirationMs) {
        localStorage.removeItem(key);
        deleteMemoryCache(key);
      }
    }
  });
}

export { fallbackImage };
